import api from "@/_helpers/api";
import { defineStore } from "pinia";
import { axiosInstance } from "@/plugins/https";
import { OsType } from "@/constants/devices";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { DeviceLabel } from "@/_store/devices-settings.module";
import { StoreModule } from "@/constants/store.ts";

export interface NewMalwareScanItem {
  daysCount: number;
  osType: OsType;
  preferredHour: number;
  labels: Array<DeviceLabel>;
}

export interface MalwareScanItem extends NewMalwareScanItem {
  id: string;
}

interface MalwareScanState {
  items: Array<MalwareScanItem>;
  showSkeletonLoader: boolean;
}

export const defaultMalwareScanState = {
  items: [],
  showSkeletonLoader: false,
};

export const useMalwareScanStore = defineStore(StoreModule.MALWARE_SCAN, {
  state: (): MalwareScanState => ({
    ...defaultMalwareScanState,
  }),
  actions: {
    async getMalwareScans(showSkeletonLoader = true) {
      this.showSkeletonLoader = showSkeletonLoader;

      const request = {
        ...api.malwareScans,
      };
      try {
        const { data } = await axiosInstance.request(request);
        const windowsData = data.windowsSettingsLabeled.map((item: MalwareScanItem) => {
          return {
            ...item,
            osType: OsType.WINDOWS,
          };
        });
        const macData = data.macOsSettingsLabeled.map((item: MalwareScanItem) => {
          return {
            ...item,
            osType: OsType.MAC_OS,
          };
        });
        const linuxData = data.linuxSettingsLabeled.map((item: MalwareScanItem) => {
          return {
            ...item,
            osType: OsType.LINUX,
          };
        });
        this.items = [...windowsData, ...macData, ...linuxData];
      } catch (e) {
        console.error(e);
      }
      this.showSkeletonLoader = false;
    },
    async createMalwareScanPolicy(payload: DialogDataConfig<MalwareScanItem>) {
      const request = {
        ...api.malwareScans,
        method: "POST",
        data: payload.item,
      };
      try {
        await axiosInstance.request(request);
        await this.getMalwareScans(false);
      } catch (e) {
        console.error(e);
      }
    },
    async editMalwareScanPolicy(payload: DialogDataConfig<MalwareScanItem>) {
      const data = {
        daysCount: payload.item.daysCount,
        osType: payload.item.osType,
        preferredHour: payload.item.preferredHour,
        labels: payload.item.labels,
      };
      const request = {
        ...api.malwareScans,
        method: "PUT",
        data: data,
        params: {
          id: payload.item.id,
        },
      };
      try {
        await axiosInstance.request(request);
        await this.getMalwareScans(false);
      } catch (e) {
        console.error(e);
      }
    },
    async deleteMalwareScanPolicy(payload: DialogDataConfig<MalwareScanItem>) {
      const request = {
        ...api.malwareScans,
        method: "DELETE",
        params: {
          id: payload.item.id,
        },
      };
      try {
        await axiosInstance.request(request);
        await this.getMalwareScans(false);
      } catch (e) {
        console.error(e);
      }
    },
  },
});
